import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { testNotificationsControllerIssueLinkTicket } from '@/generated/korza-api-client';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { getApiErrorMessage } from '@/lib/api-error';

export interface TestDeviceLinkTicket {
  ticket: string;
  expiresAt: string;
}

/**
 * Requests a one-time link ticket for the current store
 * (`POST test-notifications/link-ticket`, PUSH-09 AC 2). A mutation, never
 * cached nor stored: every opening of the link sheet and every "Tentar de
 * novo" asks for a new one. Decides by `response.status` (AD-025): 201 → the
 * ticket; 4xx → the body's message; network error or 5xx → the translated
 * generic message.
 */
export function useTestDeviceLinkTicket(): UseMutationResult<
  TestDeviceLinkTicket,
  Error,
  void
> {
  const tenant = useTenant();
  const { LL } = useI18nContext();

  return useMutation<TestDeviceLinkTicket, Error, void>({
    mutationFn: async () => {
      const generic = LL.campaignComposer.testDevice.ticketError();
      const result = await testNotificationsControllerIssueLinkTicket({
        path: { tenantSlug: tenant.slug },
      }).catch(() => {
        throw new Error(generic);
      });
      const { status } = result.response;
      if (status === 201 && result.data) {
        return { ticket: result.data.ticket, expiresAt: result.data.expiresAt };
      }
      throw new Error(
        status >= 500 ? generic : getApiErrorMessage(result.error, generic)
      );
    },
  });
}
